import Close from "@mui/icons-material/Close";
import FolderOutlined from "@mui/icons-material/FolderOutlined";
import PermMediaOutlined from "@mui/icons-material/PermMediaOutlined";
import { Box, Button, IconButton, MenuItem, TextField, Tooltip, Typography } from "@mui/material";
import { useMemo, useState, type FC } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useAsyncStateSupabaseCallback } from "../../utils/hooks/use-async-state-callback";
import type { UUID } from "../../utils/uuid";
import { useFolders } from "./hooks/use-folders";
import { SupabaseFolderService } from "./server/folder-service";
import {
  flattenTreeItems,
  folderNameFromId,
  ROOT_ID,
  transformFoldersToTreeItems,
  type UiFolderId,
} from "./utils";

type AssetFolderSelectProps = {
  selectedFolderId: UiFolderId;
  onChange: (folderId: UiFolderId) => void;
};

export const AssetFolderSelect: FC<AssetFolderSelectProps> = ({ selectedFolderId, onChange }) => {
  const { t } = useTranslation();
  const { data: folders, loading, error, reload } = useFolders();
  const [showNewFolderField, setShowNewFolderField] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const treeItems = useMemo(() => transformFoldersToTreeItems(folders ?? []), [folders]);
  const options = useMemo(() => flattenTreeItems(treeItems), [treeItems]);
  const selectedLabel = folderNameFromId(selectedFolderId, folders ?? null) ?? t("Unknown folder");
  const normalizedNewFolderName = newFolderName.trim();

  const hasDuplicateFolderName = useMemo(() => {
    if (!normalizedNewFolderName || !folders) return false;
    const parentFolderId = selectedFolderId === ROOT_ID ? null : selectedFolderId;
    return folders.some(
      (folder) =>
        folder.parent_folder_id === parentFolderId &&
        folder.name.trim() === normalizedNewFolderName,
    );
  }, [folders, normalizedNewFolderName, selectedFolderId]);

  const menuOptions = useMemo(() => {
    if (options.some((option) => option.id === selectedFolderId)) return options;
    return [{ id: selectedFolderId, label: selectedLabel, depth: 0 }, ...options];
  }, [options, selectedFolderId, selectedLabel]);

  const { loading: savingNewFolder, load: saveNewFolder } = useAsyncStateSupabaseCallback(
    async (
      { supabase },
      { name, parentFolderId }: { name: string; parentFolderId: UUID | null },
    ) => {
      const service = new SupabaseFolderService(supabase);
      const { data, error: newFolderError } = await service.insert({
        parent_folder_id: parentFolderId,
        name,
      });
      if (newFolderError) throw newFolderError;
      return data;
    },
    [],
  );

  const handleCreateFolder = (): void => {
    if (normalizedNewFolderName.length === 0 || hasDuplicateFolderName) return;
    const parentFolderId = selectedFolderId === ROOT_ID ? null : selectedFolderId;
    void saveNewFolder({
      name: normalizedNewFolderName,
      parentFolderId,
    })
      .then((createdFolder) => {
        if (!createdFolder) return;
        toast.success(t(`Folder '${createdFolder.name}' created`));
        setNewFolderName("");
        setShowNewFolderField(false);
        reload();
        onChange(createdFolder.id);
      })
      .catch((e) => {
        console.error(e);
        if (e instanceof Error && e.message.includes("folder_unique_name")) {
          toast.error(t("Folder name must be unique"));
        } else {
          toast.error(t("Error when creating folder"));
        }
      });
  };

  return (
    <Box display="flex" flexDirection="column">
      <TextField
        select
        label={t("Save to folder")}
        value={selectedFolderId}
        onChange={(event) => onChange(event.target.value as UiFolderId)}
        disabled={loading || Boolean(error)}
        error={Boolean(error)}
        helperText={error ? t("Unable to load folders") : null}
        slotProps={{
          select: {
            renderValue: () => selectedLabel,
            MenuProps: {
              PaperProps: {
                sx: { maxHeight: 360 },
              },
            },
          },
        }}
      >
        {menuOptions.map((option) => {
          const isRoot = option.id === ROOT_ID;
          return (
            <MenuItem key={option.id} value={option.id}>
              <Box display="flex" alignItems="center" gap={1} sx={{ pl: option.depth * 2 }}>
                {isRoot ? (
                  <PermMediaOutlined fontSize="small" />
                ) : (
                  <FolderOutlined fontSize="small" />
                )}
                <Typography variant="body2" noWrap>
                  {option.label}
                </Typography>
              </Box>
            </MenuItem>
          );
        })}
      </TextField>
      <Box display="flex" alignItems="center" gap={0.5} sx={{ alignSelf: "flex-start", mt: 1 }}>
        <Button
          onClick={() => {
            setNewFolderName("");
            setShowNewFolderField(true);
          }}
          variant="text"
          size="small"
          data-analytics-id="upload-file-create-new-folder"
          disabled={showNewFolderField}
          sx={{
            p: 0,
            minHeight: "auto",
            textTransform: "none",
            lineHeight: 1.5,
          }}
        >
          <Typography variant="body2">
            {t("Create new folder in '{{folderName}}'", { folderName: selectedLabel })}
          </Typography>
        </Button>
        {showNewFolderField ? (
          <Tooltip title={t("Cancel new folder creation")}>
            <IconButton
              size="small"
              data-analytics-id="upload-file-create-new-folder-cancel"
              onClick={() => {
                setShowNewFolderField(false);
                setNewFolderName("");
              }}
              aria-label={t("Cancel new folder creation")}
            >
              <Close fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : null}
      </Box>
      {showNewFolderField ? (
        <Box display="flex" alignItems="center" gap={1} sx={{ mt: 2 }}>
          <TextField
            fullWidth
            label={t("New folder name")}
            value={newFolderName}
            onChange={(event) => setNewFolderName(event.target.value)}
            error={hasDuplicateFolderName}
            helperText={
              hasDuplicateFolderName
                ? t("A folder with this name already exists in '{{folderName}}'", {
                    folderName: selectedLabel,
                  })
                : null
            }
          />
          <Button
            onClick={handleCreateFolder}
            variant="contained"
            data-analytics-id="upload-file-create-new-folder-confirm"
            disabled={
              savingNewFolder || normalizedNewFolderName.length === 0 || hasDuplicateFolderName
            }
          >
            {t("Create")}
          </Button>
        </Box>
      ) : null}
    </Box>
  );
};
